import React, { createContext, useContext } from 'react';
import type { DownsellOfferPreviewState } from '@/store/editorSlice';

/**
 * WEB-681/683/682b: shared "am I rendering inside the dashboard editor's
 * phone preview" signal for block components (PublicBlockRenderer /
 * PublicBlockRendererExtended). Blocks that trigger side effects unsafe for
 * the editor canvas (redirect navigation, fixed-position overlays, auto
 * navigation) branch on `isEditorPreview` to render an inert preview instead.
 *
 * `cookiePreviewVisible` is a companion transient UI flag (editorSlice) so
 * the cookie_consent block's inline preview can be toggled from the
 * properties panel ("Показать"/"Скрыть") without a fixed-position overlay
 * fighting the phone canvas.
 *
 * Default value = the public player: isEditorPreview false, and
 * cookiePreviewVisible true (unused by the player, which manages its own
 * banner visibility locally — see CookieConsentBlock).
 */
export interface EditorPreviewContextValue {
  isEditorPreview: boolean;
  cookiePreviewVisible: boolean;
  /** QA WEB-673: показ inline-превью downsell-попапа (кнопка «Показать»). */
  downsellPreviewVisible: boolean;
  /**
   * WEB-1753: какой из четырёх видов блока «Оффер удержания» рисовать на
   * канвасе редактора. Действует ТОЛЬКО вместе с isEditorPreview: в
   * опубликованном портале вид выбирается по настоящим данным покупателя, и
   * значение отсюда там не читается (см. DownsellOfferBlock).
   */
  downsellOfferPreviewState: DownsellOfferPreviewState;
  /**
   * WEB-2996: «замороженный» рендер — узел графа своей воронки рисует
   * настоящий экран миниатюрой, и в ней таймеры и стадии стоят (обратный
   * отсчёт, лоадер, билет с таймером). Одного isEditorPreview мало: в превью
   * конструктора отсчёт и стадии живые (WEB-978 п.6). Признак узкий: его
   * ставит только миниатюра графа; плеер и превью конструктора его не
   * передают — у них значение пустое и поведение прежнее.
   */
  frozen?: boolean;
}

const DEFAULT_EDITOR_PREVIEW_CONTEXT: EditorPreviewContextValue = {
  isEditorPreview: false,
  cookiePreviewVisible: true,
  downsellPreviewVisible: false,
  // WEB-1753: значение по умолчанию = «как было до переключателя». Плеер его
  // всё равно не читает (ветку выбирают данные покупателя).
  downsellOfferPreviewState: 'offer',
};

const EditorPreviewContext = createContext<EditorPreviewContextValue>(
  DEFAULT_EDITOR_PREVIEW_CONTEXT,
);

interface EditorPreviewProviderProps
  extends Omit<
    EditorPreviewContextValue,
    'downsellPreviewVisible' | 'downsellOfferPreviewState'
  > {
  downsellPreviewVisible?: boolean;
  downsellOfferPreviewState?: DownsellOfferPreviewState;
  frozen?: boolean;
  children: React.ReactNode;
}

export function EditorPreviewProvider({
  isEditorPreview,
  cookiePreviewVisible,
  downsellPreviewVisible = false,
  downsellOfferPreviewState = 'offer',
  frozen,
  children,
}: EditorPreviewProviderProps): React.ReactElement {
  return (
    <EditorPreviewContext.Provider
      value={{
        isEditorPreview,
        cookiePreviewVisible,
        downsellPreviewVisible,
        downsellOfferPreviewState,
        frozen,
      }}
    >
      {children}
    </EditorPreviewContext.Provider>
  );
}

export function useEditorPreview(): EditorPreviewContextValue {
  return useContext(EditorPreviewContext);
}
